import { useState, useMemo } from "react";
import { NAVY, GOLD } from "../styles/palette";
import { sb } from "../lib/supabase";
import { getPrice, getPerVisit, getDeoMonthly, DEO_PER_VISIT } from "../lib/pricing";
import { getServiceDayForClient } from "../lib/serviceArea";
import { useIsMobile } from "../lib/useIsMobile";

// PAYMENT LINK MODAL — Preview & send (invoice from DB, no editing)
export function PaymentLinkModal({ client, invoices, onClose, onSent, showToast, orgName }) {
  const isMobile = useIsMobile();
  const [sending,    setSending]    = useState(false);
  const [copying,    setCopying]    = useState(false);
  const [copied,     setCopied]     = useState(false);
  const [error,      setError]      = useState("");

  // Find the best invoice using same priority as edge function: overdue → pending → needs_approval
  const bestInvoice = useMemo(() => {
    if (!invoices) return null;
    const mine = invoices.filter(i => i.client_id === client.id);
    for (const status of ["uncollectible","draft","open"]) {
      const found = mine.filter(i=>i.status===status).sort((a,b)=>b.created_at?.localeCompare(a.created_at))[0];
      if (found) return found;
    }
    return null;
  }, [invoices, client.id]);

  // Use invoice data if available, fall back to client
  const freq      = bestInvoice?.frequency || client.frequency || "weekly";
  const dogs      = Number(bestInvoice?.dogs || client.dogs || 1);
  const monthly   = bestInvoice ? Number(bestInvoice.amount) : getPrice(freq, dogs);
  const perVisit  = getPerVisit(freq, dogs);
  const freqLabel = freq==="weekly"?"Weekly":freq==="biweekly"?"Bi-Weekly":freq==="twice-weekly"?"Twice-Weekly":"Monthly";
  const deoMonthly  = client.deo_enabled ? getDeoMonthly(client.deo_frequency||"biweekly") : 0;
  const deoPerVisit = client.deo_enabled ? DEO_PER_VISIT[client.deo_frequency||"biweekly"] : 0;
  const deoFreqLabel = client.deo_frequency==="weekly"?"Weekly":client.deo_frequency==="biweekly"?"Bi-Weekly":"Monthly";
  const grandTotal   = monthly + deoMonthly;

  // Service day — from the operator's saved service area (DB), not a hardcoded map.
  const sdRaw = getServiceDayForClient(client);
  const serviceDay = sdRaw ? sdRaw.charAt(0).toUpperCase() + sdRaw.slice(1) : "your service day";

  async function handleCopyLink() {
    setError(""); setCopying(true);
    try {
      const res = await sb.fn("create-payment-link", { client_id: client.id, link_only: true });
      if (res.error) { setError(res.error); setCopying(false); return; }
      await navigator.clipboard.writeText(res.setup_url);
      setCopied(true);
      setTimeout(()=>setCopied(false), 3000);
    } catch(e) { setError("Failed to generate link"); }
    setCopying(false);
  }

  async function handleSend() {
    setError(""); setSending(true);
    try {
      const res = await sb.fn("create-payment-link", { client_id: client.id });
      if (res.error) { setError(res.error); setSending(false); return; }
      onSent();
    } catch(e) { setError("Failed to send — please try again"); }
    setSending(false);
  }

  const hasServicePlan = client.frequency && client.dogs;

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:520}}>
        <div className="modal-header">
          <h3>🔗 Send Payment Link</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {!hasServicePlan ? (
            <div style={{background:"#ffebe6",border:"1px solid #ffbdad",borderRadius:8,padding:"16px",textAlign:"center"}}>
              <div style={{fontSize:24,marginBottom:8}}>⚠️</div>
              <div style={{fontWeight:600,color:"#bf2600",marginBottom:4}}>No service plan set</div>
              <div style={{fontSize:13,color:"#42526e"}}>Add a service plan to this client first, then send the payment link.</div>
            </div>
          ) : (
            <>
              <div style={{fontSize:13,color:"#42526e",marginBottom:16,lineHeight:1.6}}>
                This will email <strong>{client.name}</strong> a branded invoice with their service details and a secure payment button. Here's a preview of what they'll see:
              </div>

              {/* Mini email preview */}
              <div style={{background:"#f5f6f8",borderRadius:10,overflow:"hidden",border:"1px solid #e3e8ef",marginBottom:16}}>
                {/* Header */}
                <div style={{background:NAVY,padding:"14px 20px",textAlign:"center"}}>
                  <div style={{fontWeight:800,color:"white",fontSize:14}}>{orgName || "Your service provider"}</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,.4)",marginTop:2}}>Professional Dog Waste Removal</div>
                </div>
                {/* Body */}
                <div style={{padding:"16px 20px",background:"white"}}>
                  <div style={{fontWeight:700,fontSize:16,color:"#1a1f36",marginBottom:4}}>Hi {client.name.split(" ")[0]}! 👋</div>
                  <div style={{fontSize:12,color:"#42526e",marginBottom:14,lineHeight:1.5}}>Thank you for choosing Scoopers on Doody! Review your service details and tap the button to add your payment method.</div>

                  {/* Service details */}
                  <div style={{background:"#f5f6f8",borderRadius:8,padding:"12px 14px",marginBottom:14,fontSize:12}}>
                    <div style={{fontWeight:700,fontSize:10,color:"#8993a4",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Your Service Details</div>
                    {[["Client",client.name],["Address",[client.address,client.zip].filter(Boolean).join(", ")||"On file"],["Service Day",serviceDay+"s"]].map(([l,v])=>(
                      <div key={l} style={{padding:"5px 0",borderBottom:"1px solid #e3e8ef",color:"#42526e"}}>
                        {l}<br/><strong style={{color:"#1a1f36"}}>{v}</strong>
                      </div>
                    ))}
                  </div>

                  {/* Line items — Stripe is the source of truth.
                      Render bestInvoice.line_items (mirrored by webhook from
                      Stripe) when available so cleanup + deo + any custom
                      lines all show with their real descriptions and amounts.
                      Fall back to the client-derived breakdown only if no
                      invoice exists yet (e.g., subscription not finalized). */}
                  <div style={{borderRadius:6,overflow:"hidden",border:"1px solid #e3e8ef",marginBottom:12}}>
                    <div style={{background:NAVY,display:"flex",justifyContent:"space-between",padding:"6px 12px"}}>
                      <span style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,.7)",textTransform:"uppercase",letterSpacing:".05em"}}>Service</span>
                      <span style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,.7)",textTransform:"uppercase",letterSpacing:".05em"}}>Amount</span>
                    </div>
                    {Array.isArray(bestInvoice?.line_items) && bestInvoice.line_items.length > 0 ? (
                      bestInvoice.line_items.map((li, idx) => {
                        const isCredit = Number(li.amount) < 0;
                        const isLast = idx === bestInvoice.line_items.length - 1;
                        return (
                          <div key={li.line_id || idx} style={{padding:"10px 12px",borderBottom: isLast ? "none" : "1px solid #f0f2f5",display:"flex",justifyContent:"space-between",alignItems:"flex-start",background: isCredit ? "#f0fdf4" : undefined}}>
                            <div style={{flex:1,paddingRight:8}}>
                              <div style={{fontSize:13,fontWeight:600,color: isCredit ? "#166534" : "#1a1f36"}}>{li.description || "Service"}</div>
                              {li.quantity > 1 && <div style={{fontSize:11,color:"#8993a4"}}>Qty: {li.quantity}</div>}
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontSize:13,fontWeight:700,color: isCredit ? "#166534" : "#1a1f36"}}>
                                {isCredit ? "-" : ""}${Math.abs(Number(li.amount)).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <>
                        <div style={{padding:"10px 12px",borderBottom:client.deo_enabled?"1px solid #f0f2f5":"none",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div>
                            <div style={{fontSize:13,fontWeight:600,color:"#1a1f36"}}>{freqLabel} Dog Waste Removal</div>
                            <div style={{fontSize:11,color:"#8993a4"}}>{dogs} dog{dogs>1?"s":""}{serviceDay ? " · "+serviceDay+"s" : ""}</div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:13,color:"#8993a4"}}>${perVisit.toFixed(2)}/visit</div>
                            <div style={{fontSize:11,color:"#b3bac5"}}>~${monthly.toFixed(2)}/mo</div>
                          </div>
                        </div>
                        {client.deo_enabled&&(
                          <div style={{padding:"10px 12px",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                            <div style={{fontSize:13,fontWeight:600,color:"#1a1f36"}}>{deoFreqLabel} Deodorizer</div>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:13,color:"#8993a4"}}>${deoPerVisit.toFixed(2)}/visit</div>
                              <div style={{fontSize:11,color:"#b3bac5"}}>~${deoMonthly.toFixed(2)}/mo</div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Total — label adapts: proration drafts are one-time
                      first charges, recurring invoices are monthly. We
                      detect proration by looking for the word in any
                      line description. */}
                  {(()=>{
                    const isProration = Array.isArray(bestInvoice?.line_items)
                      && bestInvoice.line_items.some(li => /proration/i.test(li.description || ""));
                    const totalAmt = bestInvoice ? Number(bestInvoice.amount) : grandTotal;
                    return (
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",borderTop:"2px solid "+NAVY,paddingTop:10,marginBottom:14}}>
                        <span style={{fontWeight:700,fontSize:14,color:NAVY}}>{isProration ? "Due now" : "Total"}</span>
                        <div style={{textAlign:"right"}}>
                          <span style={{fontWeight:800,fontSize:20,color:NAVY}}>${totalAmt.toFixed(2)}</span>
                          {!isProration && <span style={{fontSize:12,color:"#8993a4"}}>/month</span>}
                          <div style={{fontSize:10,color:"#b3bac5"}}>
                            {isProration
                              ? `First-month proration · monthly billing starts on the 1st`
                              : `Billed monthly · ${freqLabel} service`}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Button preview */}
                  <div style={{textAlign:"center",padding:"4px 0 8px"}}>
                    <div style={{display:"inline-block",background:GOLD,color:NAVY,fontWeight:800,fontSize:14,padding:"12px 28px",borderRadius:8}}>
                      💳 Add Payment Method
                    </div>
                    <div style={{fontSize:10,color:"#b3bac5",marginTop:6}}>🔒 Secured by Stripe</div>
                  </div>
                </div>
              </div>

              <div style={{background:"#deebff",border:"1px solid #b3d4ff",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#0747a6"}}>
                📧 Will be sent to: <strong>{client.email}</strong>
              </div>
              {bestInvoice&&<div style={{background:"#f5f6f8",border:"1px solid #e3e8ef",borderRadius:6,padding:"6px 12px",fontSize:11,color:"#8993a4",marginTop:6}}>
                📄 Invoice: <strong style={{color:NAVY}}>{bestInvoice.invoice_number}</strong> · <strong>${Number(bestInvoice.amount).toFixed(2)}</strong> · <span style={{textTransform:"capitalize"}}>{bestInvoice.status?.replace("_"," ")}</span>
              </div>}

              {error&&<div style={{background:"#ffebe6",border:"1px solid #ffbdad",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#bf2600",marginTop:10}}>⚠️ {error}</div>}
            </>
          )}
        </div>
        {copied&&<div style={{background:"#e3fcef",border:"1px solid #abf5d1",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#006644",margin:isMobile?"0 16px 8px":"0 24px 8px",textAlign:"center"}}>✅ Link copied! Paste it into your text message.</div>}
        {/* Phone: three nowrap buttons don't fit a 375px footer — Cancel + Copy share a
            row, Send Email gets its own full-width row. */}
        <div className="modal-footer" style={isMobile?{flexWrap:"wrap"}:undefined}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {hasServicePlan&&(
            <button className="btn btn-ghost" onClick={handleCopyLink} disabled={copying||sending} style={{color:NAVY,...(isMobile?{flex:"1 1 auto",justifyContent:"center"}:{})}}>
              {copying?"Generating...":copied?"✓ Copied!":"📱 Copy Link"}
            </button>
          )}
          {hasServicePlan&&(
            <button className="btn btn-gold" onClick={handleSend} disabled={sending||copying||!client.email} style={isMobile?{flex:"1 1 100%",justifyContent:"center"}:undefined}>
              {sending?"Sending...":"📧 Send Email"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
